import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, DollarSign, TrendingUp, CreditCard, LogOut, Search, ExternalLink,
  CheckCircle, Info, Eye, EyeOff, Check, X, Pencil, Trash2, MessageSquare,
  ChevronDown, ChevronUp, ArrowUpDown, MapPin, Briefcase, Calendar, Mail, Phone, Globe,
  Send, Filter, FileText, UserPlus, BarChart3, Database, Download, Sparkles, Clock, Star, Shield, Film,
  Banknote, Landmark, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import FunnelAnalytics from '../components/FunnelAnalytics';
import SuccessStoriesAdmin from '../components/SuccessStoriesAdmin';

// ==================== VIDEO PLAYER WITH PLAY OVERLAY ====================
const VideoPlayer = ({ url, testId }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    vid.load();
    vid.play().then(() => {
      setPlaying(true);
      vid.muted = false;
    }).catch(() => {
      vid.controls = true;
      setPlaying(true);
    });
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden aspect-video cursor-pointer group" onClick={!playing ? handlePlay : undefined} data-testid={testId}>
      <video
        ref={videoRef}
        src={url}
        controls={playing}
        className="w-full h-full object-contain"
        preload="none"
        playsInline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg className="w-6 h-6 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
          <div className="absolute bottom-3 left-3 text-[10px] text-white/60">Click to play</div>
        </div>
      )}
    </div>
  );
};

// ==================== EXPANDABLE USER ROW ====================
const UserRow = ({ u, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const isTrial = u.subscription_status === 'trial';

  return (
    <>
      <tr
        className="border-b border-gray-200/50 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`user-row-${u.id}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            <div>
              <p className="text-gray-900 font-medium">{u.full_name}</p>
              <p className="text-gray-500 text-sm">{u.email}</p>
            </div>
          </div>
        </td>
        <td className="py-3 px-4 text-gray-600 text-sm">{(u.profession_id || '').replace(/_/g, ' ')}</td>
        <td className="py-3 px-4 text-gray-600 text-sm">{u.city}{u.state ? `, ${u.state}` : ''}</td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-1">
            {u.account_type === 'business' ? (
              <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-indigo-50 text-[10px]">Business</Badge>
            ) : (
              <Badge variant="outline" className="border-gray-300 text-gray-600 text-[10px]">Individual</Badge>
            )}
            {u.is_premium ? (
              <Badge variant="default" className="bg-green-600">Premium</Badge>
            ) : isTrial ? (
              <Badge variant="default" className="bg-blue-600">Trial</Badge>
            ) : (
              <Badge variant="secondary">Free</Badge>
            )}
          </div>
        </td>
        <td className="py-3 px-4">
          <span className={isTrial ? 'text-gray-400 line-through' : 'text-green-400'}>
            ${u.total_earnings?.toFixed(2) || '0.00'}
          </span>
          {isTrial && <span className="text-gray-400 text-xs ml-1">(trial)</span>}
        </td>
        <td className="py-3 px-4 text-gray-500 text-sm">
          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-blue-400 h-8 w-8 p-0" onClick={() => onEdit(u)} data-testid={`edit-user-${u.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-red-400 h-8 w-8 p-0" onClick={() => onDelete(u)} data-testid={`delete-user-${u.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50" data-testid={`user-details-${u.id}`}>
          <td colSpan="7" className="px-4 py-4">
            <div className="grid md:grid-cols-3 gap-4 pl-6">
              <div className="space-y-2">
                <h4 className="text-gray-600 font-medium text-sm mb-2 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />Professional Info</h4>
                <div className="space-y-1.5 text-sm">
                  <p className="text-gray-500">Company: <span className="text-gray-900">{u.company || 'Not set'}</span></p>
                  <p className="text-gray-500">Profession: <span className="text-gray-900">{(u.custom_profession || u.profession_id || '').replace(/_/g, ' ')}</span></p>
                  <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{u.street_address ? `${u.street_address}, ` : ''}{u.city || 'N/A'}, {u.state || 'N/A'} {u.zip_code || ''}</p>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-gray-600 font-medium text-sm mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Contact</h4>
                <div className="space-y-1.5 text-sm">
                  <p className="text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</p>
                  <p className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone || 'Not set'}</p>
                  <p className="text-gray-500 flex items-center gap-1"><Globe className="w-3 h-3" />{u.website || 'Not set'}</p>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-gray-600 font-medium text-sm mb-2 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />Account Details</h4>
                <div className="space-y-1.5 text-sm">
                  <p className="text-gray-500">Status: <span className="text-gray-900">{u.subscription_status || 'inactive'}</span></p>
                  <p className="text-gray-500">Ad Slots: <span className="text-gray-900">{(u.ad_slots_owned || 1) + (u.additional_ad_slots || 0)}</span></p>
                  <p className="text-gray-500">Earnings: <span className={isTrial ? 'text-gray-400' : 'text-green-400'}>${u.total_earnings?.toFixed(2) || '0.00'}{isTrial ? ' (trial - not real)' : ''}</span></p>
                  <p className="text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</p>
                  {u.trial_end_date && <p className="text-gray-500">Trial ends: {new Date(u.trial_end_date).toLocaleDateString()}</p>}
                  {u.stripe_connect_id && <p className="text-gray-500">Stripe Connect: <span className="text-green-400">Connected</span></p>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ==================== MAIN ADMIN DASHBOARD ====================
export const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [processingPayout, setProcessingPayout] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteUser, setDeleteUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmSearch, setCrmSearch] = useState('');
  const [crmSort, setCrmSort] = useState({ field: 'full_name', dir: 'asc' });
  const [promoSubject, setPromoSubject] = useState('');
  const [promoBody, setPromoBody] = useState('');
  const [promoFilter, setPromoFilter] = useState('all');
  const [sendingPromo, setSendingPromo] = useState(false);
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoPublished, setVideoPublished] = useState(false);
  const [videoExists, setVideoExists] = useState(false);
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoType, setVideoType] = useState('promo');
  const [videoDescription, setVideoDescription] = useState('');
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [demoAnalytics, setDemoAnalytics] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [genMethod, setGenMethod] = useState('slideshow'); // 'sora2' | 'slideshow'
  const [activeTab, setActiveTab] = useState('overview');
  const hasLoadedOnce = useRef(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const resumedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('connectmail_admin_token');
    if (storedToken) {
      api.adminVerify(storedToken)
        .then(() => { setAdminToken(storedToken); setIsLoggedIn(true); })
        .catch(() => { localStorage.removeItem('connectmail_admin_token'); setLoading(false); });
    } else { setLoading(false); }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn && adminToken) fetchAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, adminToken]);

  // Resume video generation polling if page was refreshed/navigated away
  useEffect(() => {
    if (!isLoggedIn || !adminToken || resumedRef.current) return;
    resumedRef.current = true;
    try {
      const saved = localStorage.getItem('connectmail_video_job');
      if (!saved) return;
      const { jobId, startedAt } = JSON.parse(saved);
      // Only resume if started less than 10 minutes ago
      if (Date.now() - startedAt > 10 * 60 * 1000) {
        localStorage.removeItem('connectmail_video_job');
        return;
      }
      setVideoJobId(jobId);
      setVideoGenerating(true);
      setVideoStatus({ status: 'generating', progress: 'Resuming... checking status...', percent: 5 });
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedTime(elapsed);
      const st = Date.now() - elapsed * 1000;
      timerRef.current = setInterval(() => setElapsedTime(Math.floor((Date.now() - st) / 1000)), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.adminGetVideoStatus(adminToken, jobId);
          setVideoStatus(status);
          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            pollRef.current = null;
            timerRef.current = null;
            setVideoGenerating(false);
            localStorage.removeItem('connectmail_video_job');
            if (status.status === 'completed') {
              toast.success('Video generated!');
              setVideoExists(true);
              const videosData = await api.adminListVideos(adminToken);
              setGeneratedVideos(videosData.videos || []);
            } else {
              toast.error('Video generation failed: ' + status.progress);
            }
          }
        } catch {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          pollRef.current = null;
          timerRef.current = null;
          setVideoGenerating(false);
          localStorage.removeItem('connectmail_video_job');
        }
      }, 5000);
    } catch {
      localStorage.removeItem('connectmail_video_job');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, adminToken]);

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const data = await api.adminLogin(email, password);
      localStorage.setItem('connectmail_admin_token', data.token);
      setAdminToken(data.token);
      setIsLoggedIn(true);
      toast.success('Welcome, Admin!');
    } catch { toast.error('Invalid admin credentials'); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('connectmail_admin_token');
    setAdminToken(null); setIsLoggedIn(false); setEmail(''); setPassword('');
  };

  const fetchAdminData = async (isRefresh = false) => {
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const [dashData, payoutsData, feedbackData, contactsData, campaignsData, crmData] = await Promise.all([
        api.adminGetDashboard(adminToken),
        api.adminGetPayouts(adminToken),
        api.adminGetFeedback(adminToken),
        api.adminGetContacts(adminToken).catch(() => ({ contacts: [], stats: {} })),
        api.adminGetPromoCampaigns(adminToken).catch(() => ({ campaigns: [] })),
        api.adminGetCrmDatabase(adminToken).catch(() => ({ users: [] })),
      ]);
      setStats(dashData.stats);
      setUsers(dashData.users || []);
      setTransactions(dashData.transactions || []);
      setPayouts(payoutsData.payouts || []);
      setFeedback(feedbackData.feedback || []);
      setContacts(contactsData.contacts || []);
      setContactStats(contactsData.stats || {});
      setCampaigns(campaignsData.campaigns || []);
      setCrmUsers(crmData.users || []);
      // Fetch video publish status and all generated videos
      try {
        const vData = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promo-video-url`).then(r => r.json());
        setVideoExists(vData.exists || false);
        setVideoPublished(vData.published || false);
        const videosData = await api.adminListVideos(adminToken);
        setGeneratedVideos(videosData.videos || []);
      } catch {}
    } catch (error) {
      if (error?.response?.status === 401) { handleLogout(); toast.error('Session expired'); }
    } finally { setLoading(false); hasLoadedOnce.current = true; }
  };

  const handleFeedbackStatusChange = async (feedbackId, status) => {
    try { await api.adminUpdateFeedback(adminToken, feedbackId, { status }); toast.success('Updated'); setFeedback(prev => prev.map(f => f.id === feedbackId ? { ...f, status } : f)); } catch { toast.error('Failed'); }
  };

  const handleSendPromoEmail = async (testMode = false) => {
    if (!promoSubject.trim() || !promoBody.trim()) { toast.error('Subject and body are required'); return; }
    setSendingPromo(true);
    try {
      const resp = await api.adminSendPromoEmail(adminToken, promoSubject, promoBody, promoFilter, testMode);
      if (testMode) {
        toast.info(resp.message);
      } else {
        toast.success(resp.message);
        setPromoSubject(''); setPromoBody('');
        const camp = await api.adminGetPromoCampaigns(adminToken);
        setCampaigns(camp.campaigns || []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send'); }
    setSendingPromo(false);
  };
  const handleDeleteFeedback = async (feedbackId) => {
    try { await api.adminDeleteFeedback(adminToken, feedbackId); toast.success('Deleted'); setFeedback(prev => prev.filter(f => f.id !== feedbackId)); } catch { toast.error('Failed'); }
  };
  const filteredFeedback = feedback.filter(f => feedbackFilter === 'all' || f.status === feedbackFilter);

  const handlePayoutAction = async (payoutId, action) => {
    setProcessingPayout(payoutId);
    try { await api.adminPayoutAction(adminToken, payoutId, action); toast.success(`Payout ${action}d`); fetchAdminData(); } catch { toast.error(`Failed to ${action}`); }
    finally { setProcessingPayout(null); }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name || '', email: u.email || '', profession_id: u.profession_id || '', company: u.company || '', city: u.city || '', state: u.state || '', is_premium: u.is_premium || false });
  };
  const handleEditUser = async () => {
    setActionLoading(true);
    try { await api.adminUpdateUser(adminToken, editUser.id, editForm); toast.success('User updated'); setEditUser(null); fetchAdminData(); } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };
  const handleDeleteUser = async () => {
    setActionLoading(true);
    try { await api.adminDeleteUser(adminToken, deleteUser.id); toast.success('User deleted'); setDeleteUser(null); fetchAdminData(); } catch { toast.error('Failed'); }
    finally { setActionLoading(false); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filteredUsers = users
    .filter(user => {
      const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (userFilter === 'all') return true;
      if (userFilter === 'premium') return user.is_premium === true;
      if (userFilter === 'trial') return user.subscription_status === 'trial';
      if (userFilter === 'free') return !user.is_premium && user.subscription_status !== 'trial';
      if (userFilter === 'business') return user.account_type === 'business';
      if (userFilter === 'individual') return user.account_type !== 'business';
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name': aVal = (a.full_name || '').toLowerCase(); bVal = (b.full_name || '').toLowerCase(); break;
        case 'profession': aVal = (a.profession_id || '').toLowerCase(); bVal = (b.profession_id || '').toLowerCase(); break;
        case 'status':
          const getStatusRank = (u) => u.is_premium ? 3 : u.subscription_status === 'trial' ? 2 : 1;
          aVal = getStatusRank(a); bVal = getStatusRank(b); break;
        case 'earnings': aVal = a.total_earnings || 0; bVal = b.total_earnings || 0; break;
        case 'created_at': aVal = a.created_at || ''; bVal = b.created_at || ''; break;
        default: aVal = a.created_at || ''; bVal = b.created_at || '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const SortHeader = ({ field, children }) => (
    <th
      className="text-left py-3 px-4 text-gray-500 font-medium cursor-pointer hover:text-gray-900 transition-colors select-none"
      onClick={() => toggleSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortField === field ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </span>
    </th>
  );

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-gray-200 shadow-xl">
          <CardHeader className="text-center">
            <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-12 h-12 rounded-lg bg-gray-900 p-1 mx-auto mb-4" />
            <CardTitle className="text-gray-900 text-2xl">Admin Dashboard</CardTitle>
            <CardDescription className="text-gray-500">ConnectMail Platform Management</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Admin Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@connectmail.com" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="admin-email-input" onKeyDown={(e) => e.key === 'Enter' && handleLogin()} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="bg-gray-50 border-gray-300 text-gray-900 pr-10" data-testid="admin-password-input" onKeyDown={(e) => e.key === 'Enter' && handleLogin()} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button onClick={handleLogin} className="w-full bg-gray-900 hover:bg-gray-800 text-white" disabled={loginLoading} data-testid="admin-login-btn">{loginLoading ? 'Signing in...' : 'Login to Admin'}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center"><div className="animate-pulse text-gray-500">Loading dashboard...</div></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-icon.png" alt="ConnectMail" className="w-10 h-10 rounded-lg bg-gray-900 p-1" />
            <div><h1 className="text-gray-900 font-semibold">ConnectMail Admin</h1><p className="text-gray-500 text-xs">Platform Management</p></div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} className="border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50"><ExternalLink className="w-4 h-4 mr-2" />View Site</Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-500 hover:text-gray-900" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 shadow-sm flex flex-wrap gap-1 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-users-tab">All Accounts</TabsTrigger>
            <TabsTrigger value="feedback" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-feedback-tab">Feedback</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-payments-tab">Payments</TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-payouts-tab">Payouts</TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-transparent data-[state=active]:text-emerald-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 text-gray-500 rounded-none" data-testid="admin-contacts-tab">
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="crm" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-crm-tab">
              <Database className="w-3.5 h-3.5 mr-1.5" />
              CRM Database
            </TabsTrigger>
            <TabsTrigger value="stripe" className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-gray-500 rounded-none" data-testid="admin-stripe-tab">Stripe Setup</TabsTrigger>
            <TabsTrigger value="ambassadors" className="data-[state=active]:bg-transparent data-[state=active]:text-amber-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-amber-600 text-gray-500 rounded-none" data-testid="admin-ambassadors-tab">
              <Star className="w-3.5 h-3.5 mr-1.5" />
              Ambassadors
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-transparent data-[state=active]:text-purple-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-purple-600 text-gray-500 rounded-none" data-testid="admin-videos-tab"
              onClick={async () => {
                try {
                  const videosData = await api.adminListVideos(adminToken);
                  setGeneratedVideos(videosData.videos || []);
                } catch {}
              }}
            >
              <Film className="w-3.5 h-3.5 mr-1.5" />
              Video Library
            </TabsTrigger>
            <TabsTrigger value="demos" className="data-[state=active]:bg-transparent data-[state=active]:text-cyan-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-cyan-600 text-gray-500 rounded-none" data-testid="admin-demos-tab"
              onClick={async () => {
                try {
                  const data = await api.adminGetDemoAnalytics(adminToken);
                  setDemoAnalytics(data);
                } catch {}
              }}
            >
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
              Demo Analytics
            </TabsTrigger>
            <TabsTrigger value="funnel" className="data-[state=active]:bg-transparent data-[state=active]:text-rose-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-rose-600 text-gray-500 rounded-none" data-testid="admin-funnel-tab">
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
              Funnel
            </TabsTrigger>
            <TabsTrigger value="stories" className="data-[state=active]:bg-transparent data-[state=active]:text-emerald-600 data-[state=active]:font-bold data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 text-gray-500 rounded-none" data-testid="admin-stories-tab">
              <Film className="w-3.5 h-3.5 mr-1.5" />
              Success Stories
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Subscribers', value: stats?.total_users || 0, icon: Users, color: 'blue', testid: 'stat-total-users' },
                { label: 'Premium Users', value: stats?.premium_users || 0, icon: CheckCircle, color: 'emerald', testid: 'stat-premium-users' },
                { label: 'Business Accounts', value: stats?.business_accounts || 0, icon: Briefcase, color: 'indigo', testid: 'stat-business-accounts' },
                { label: 'Individual Accounts', value: stats?.individual_accounts || 0, icon: Users, color: 'slate', testid: 'stat-individual-accounts' },
                { label: 'Real Revenue', value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'emerald', testid: 'stat-revenue' },
                { label: 'Pending Payouts', value: `$${stats?.pending_payouts?.toFixed(2) || '0.00'}`, icon: TrendingUp, color: 'amber', testid: 'stat-pending-payouts' },
              ].map(({ label, value, icon: Icon, color, testid }) => (
                <Card key={testid} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">{label}</p>
                        <p className={`text-3xl font-bold ${color === 'emerald' ? 'text-emerald-600' : color === 'amber' ? 'text-amber-600' : color === 'indigo' ? 'text-indigo-600' : 'text-gray-900'}`} data-testid={testid}>{value}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color === 'emerald' ? 'bg-emerald-50' : color === 'amber' ? 'bg-amber-50' : color === 'indigo' ? 'bg-indigo-50' : 'bg-blue-50'}`}>
                        <Icon className={`w-6 h-6 ${color === 'emerald' ? 'text-emerald-500' : color === 'amber' ? 'text-amber-500' : color === 'indigo' ? 'text-indigo-500' : 'text-blue-500'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="bg-white border-gray-200">
              <CardHeader><CardTitle className="text-gray-900">Platform Overview</CardTitle></CardHeader>
              <CardContent className="text-gray-600">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Revenue Model</h4>
                    <ul className="text-sm space-y-1 text-gray-500">
                      <li>$20/month subscription fee per user</li>
                      <li>$20/month per additional ad slot</li>
                      <li>Platform keeps 50% of ad revenue</li>
                      <li>Users earn 50% of ads placed on their signatures</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Key Metrics</h4>
                    <ul className="text-sm space-y-1 text-gray-500">
                      <li>MRR: ${((stats?.premium_users || 0) * 20).toFixed(2)}</li>
                      <li>Avg. Revenue Per User: $20.00</li>
                      <li>Conversion Rate: {stats?.total_users ? ((stats?.premium_users / stats?.total_users) * 100).toFixed(1) : 0}%</li>
                      <li className="text-yellow-400 text-xs mt-2">Note: Trial user earnings excluded from revenue calculations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Maintenance Tools */}
            <Card className="bg-white border-gray-200 shadow-sm mt-4">
              <CardContent className="py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Database Maintenance</h4>
                    <p className="text-xs text-gray-500">Remove all test/automated QA data</p>
                  </div>
                  <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50 shrink-0" data-testid="clear-all-test-data-btn" onClick={async () => {
                    if (window.confirm('This will permanently delete all test users, groups, events, and their associated data. Continue?')) {
                      try {
                        const result = await api.adminClearTestData(adminToken);
                        toast.success(result.message || 'Test data cleared');
                        fetchAdminData();
                      } catch { toast.error('Failed to clear test data'); }
                    }
                  }}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Clear Test Data</Button>
                </div>
              
              </CardContent>
            </Card>
          </TabsContent>

          {/* ALL ACCOUNTS TAB — SORTABLE + EXPANDABLE */}
          <TabsContent value="users">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-gray-900">All Accounts</CardTitle>
                    <CardDescription className="text-gray-500">
                      {users.length} total &middot; {users.filter(u => u.account_type === 'business').length} business &middot; {users.filter(u => u.account_type !== 'business').length} individual &middot; {users.filter(u => u.is_premium).length} premium
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm text-gray-900" data-testid="user-filter">
                      <option value="all">All Accounts</option>
                      <option value="business">Business Only</option>
                      <option value="individual">Individual Only</option>
                      <option value="premium">Premium Only</option>
                      <option value="trial">Trial Only</option>
                      <option value="free">Free Only</option>
                    </select>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-gray-50 border-gray-300 text-gray-900 w-64" data-testid="admin-search-input" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-12"><Users className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-500">No accounts found</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="accounts-table">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <SortHeader field="name">User</SortHeader>
                          <SortHeader field="profession">Profession</SortHeader>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Location</th>
                          <SortHeader field="status">Status</SortHeader>
                          <SortHeader field="earnings">Earnings</SortHeader>
                          <SortHeader field="created_at">Joined</SortHeader>
                          <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <UserRow key={u.id} u={u} onEdit={openEditUser} onDelete={setDeleteUser} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEEDBACK TAB */}
          <TabsContent value="feedback">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5" />User Feedback</CardTitle>
                    <CardDescription className="text-gray-500">Review and manage feedback from users</CardDescription>
                  </div>
                  <select value={feedbackFilter} onChange={(e) => setFeedbackFilter(e.target.value)} className="bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm text-gray-900" data-testid="feedback-filter">
                    <option value="all">All</option><option value="new">New</option><option value="reviewed">Reviewed</option><option value="resolved">Resolved</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {filteredFeedback.length === 0 ? (
                  <div className="text-center py-12"><MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-500">No feedback yet</p></div>
                ) : (
                  <div className="space-y-4">
                    {filteredFeedback.map((item) => (
                      <div key={item.id} className="bg-gray-100/50 rounded-lg p-4" data-testid={`feedback-item-${item.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={item.category === 'bug' ? 'destructive' : item.category === 'feature' ? 'default' : 'secondary'} className="text-xs">{item.category}</Badge>
                              <Badge variant={item.status === 'new' ? 'destructive' : item.status === 'reviewed' ? 'default' : 'secondary'} className="text-xs">{item.status}</Badge>
                            </div>
                            <h4 className="text-gray-900 font-medium">{item.subject}</h4>
                            <p className="text-gray-500 text-sm mt-1">{item.message}</p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                              <span>{item.user_name}</span><span>{item.user_email}</span><span>{new Date(item.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select value={item.status} onChange={(e) => handleFeedbackStatusChange(item.id, e.target.value)} className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-900" data-testid={`feedback-status-${item.id}`}>
                              <option value="new">New</option><option value="reviewed">Reviewed</option><option value="resolved">Resolved</option>
                            </select>
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-red-400 h-8 w-8 p-0" onClick={() => { if (window.confirm('Delete this feedback?')) handleDeleteFeedback(item.id); }} data-testid={`delete-feedback-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAYMENTS TAB */}
          <TabsContent value="payments">
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle className="text-gray-900">Payment Transactions</CardTitle><CardDescription className="text-gray-500">All platform payment activity</CardDescription></div>
                {transactions.some(tx => tx.status === 'pending') && (
                  <Button variant="outline" size="sm" className="border-red-500/50 text-red-400 hover:bg-red-500/20" data-testid="clear-test-data-btn" onClick={async () => {
                    if (window.confirm('Clear all pending test transactions?')) {
                      try { const result = await api.adminClearTestData(adminToken); toast.success(`Cleared ${result.deleted_transactions} transactions and ${result.deleted_payouts} payouts`); fetchAdminData(); } catch { toast.error('Failed'); }
                    }
                  }}><Trash2 className="w-3 h-3 mr-1" />Clear Test Data</Button>
                )}
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12"><CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-500">No transactions yet</p></div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-100/50 rounded-lg">
                        <div><p className="text-gray-900 font-medium">{tx.user_email || 'Unknown'}</p><p className="text-gray-500 text-sm capitalize">{tx.package_type?.replace('_', ' ')}</p></div>
                        <div className="text-right"><p className="text-green-400 font-medium">${tx.amount?.toFixed(2)}</p><Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className="text-xs">{tx.status}</Badge></div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAYOUTS TAB */}
          <TabsContent value="payouts">
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-white border-gray-200"><CardContent className="p-6 text-center"><p className="text-gray-500 text-sm mb-1">Real Earnings Credited</p><p className="text-2xl font-bold text-green-400">${stats?.total_earnings_paid?.toFixed(2) || '0.00'}</p><p className="text-gray-400 text-xs mt-1">Excludes trial users</p></CardContent></Card>
                <Card className="bg-white border-gray-200"><CardContent className="p-6 text-center"><p className="text-gray-500 text-sm mb-1">Pending Payouts</p><p className="text-2xl font-bold text-yellow-400">${stats?.pending_payouts?.toFixed(2) || '0.00'}</p></CardContent></Card>
                <Card className="bg-white border-gray-200"><CardContent className="p-6 text-center"><p className="text-gray-500 text-sm mb-1">Revenue Split</p><p className="text-2xl font-bold text-gray-900">50 / 50</p><p className="text-gray-400 text-xs">User / Platform</p></CardContent></Card>
              </div>
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-green-500" />
                    Payout Requests
                  </CardTitle>
                  <CardDescription>Approve payouts to send money via PayPal or Stripe Connect</CardDescription>
                </CardHeader>
                <CardContent>
                  {payouts.length === 0 ? (
                    <div className="text-center py-12"><DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-500">No payout requests yet</p><p className="text-gray-400 text-xs mt-1">When users earn ad revenue and request payouts, they'll appear here</p></div>
                  ) : (
                    <div className="space-y-3">
                      {payouts.map((p) => (
                        <div key={p.id} className="flex items-center justify-between p-4 bg-gray-100/50 rounded-lg border border-gray-200" data-testid={`payout-${p.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-gray-900 font-medium">{p.user_name}</p>
                              {p.payout_method === 'paypal' ? (
                                <Badge variant="outline" className="text-blue-600 border-blue-300 text-[10px]">
                                  <svg width="12" height="12" viewBox="0 0 24 24" className="mr-1"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" fill="#253B80"/></svg>
                                  PayPal
                                </Badge>
                              ) : p.payout_method === 'stripe_connect' ? (
                                <Badge variant="outline" className="text-green-600 border-green-300 text-[10px]">
                                  <Landmark className="w-3 h-3 mr-1" />Bank
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500 border-gray-300 text-[10px]">Credit</Badge>
                              )}
                            </div>
                            <p className="text-gray-500 text-sm">{p.user_email}</p>
                            {p.payout_method === 'paypal' && p.paypal_email && (
                              <p className="text-blue-500 text-xs mt-0.5">PayPal: {p.paypal_email}</p>
                            )}
                            <p className="text-gray-400 text-xs mt-1">
                              {new Date(p.created_at).toLocaleDateString()}
                              {p.paypal_batch_id && <span className="ml-2">Batch: {p.paypal_batch_id}</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="text-green-500 font-bold text-lg">${p.amount?.toFixed(2)}</p>
                            {p.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={() => handlePayoutAction(p.id, 'approve')} disabled={processingPayout === p.id} data-testid={`approve-payout-${p.id}`}>
                                  {processingPayout === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Send</>}
                                </Button>
                                <Button size="sm" variant="destructive" className="text-xs" onClick={() => handlePayoutAction(p.id, 'reject')} disabled={processingPayout === p.id} data-testid={`reject-payout-${p.id}`}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : p.status === 'paypal_failed' ? (
                              <Badge variant="destructive">PayPal Failed</Badge>
                            ) : (
                              <Badge variant={p.status === 'approved' ? 'default' : 'destructive'}>{p.status}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CRM CONTACTS TAB */}
          <TabsContent value="contacts">
            <div className="space-y-6">
              {/* CRM Stats */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Total Contacts', value: contactStats.total || 0, icon: Users, color: 'text-blue-400' },
                  { label: 'Invited', value: contactStats.invited || 0, icon: Send, color: 'text-green-400' },
                  { label: 'Registered', value: contactStats.registered || 0, icon: UserPlus, color: 'text-purple-400' },
                  { label: 'Pending', value: contactStats.pending || 0, icon: Mail, color: 'text-amber-400' },
                ].map((s, i) => (
                  <Card key={i} className="bg-white border-gray-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <s.icon className={`w-8 h-8 ${s.color}`} />
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Send Promotional Email */}
              <Card className="bg-white border-gray-200" data-testid="promo-email-card">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Send className="w-5 h-5 text-green-400" />
                    Send Promotional Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Recipients</label>
                      <select
                        value={promoFilter}
                        onChange={(e) => setPromoFilter(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm"
                        data-testid="promo-filter"
                      >
                        <option value="all">All Users</option>
                        <option value="premium">Premium Users Only</option>
                        <option value="free">Free Users Only</option>
                        <option value="trial">Trial Users Only</option>
                        <option value="contacts">Uninvited Contacts Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Subject</label>
                      <input
                        value={promoSubject}
                        onChange={(e) => setPromoSubject(e.target.value)}
                        placeholder="Your email subject line..."
                        className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm"
                        data-testid="promo-subject"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Email Body</label>
                    <textarea
                      value={promoBody}
                      onChange={(e) => setPromoBody(e.target.value)}
                      placeholder="Write your promotional message here... (HTML supported)"
                      rows={6}
                      className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm resize-none"
                      data-testid="promo-body"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleSendPromoEmail(true)}
                      disabled={sendingPromo}
                      className="border-gray-300 text-gray-600 hover:bg-gray-100"
                      data-testid="promo-test-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Test (Preview Count)
                    </Button>
                    <Button
                      onClick={() => handleSendPromoEmail(false)}
                      disabled={sendingPromo || !promoSubject || !promoBody}
                      className="bg-green-600 hover:bg-green-700 text-gray-900"
                      data-testid="promo-send-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingPromo ? 'Sending...' : 'Send Campaign'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Campaign History */}
              {campaigns.length > 0 && (
                <Card className="bg-white border-gray-200" data-testid="campaign-history">
                  <CardHeader>
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      Campaign History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {campaigns.map((c, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-100/50 rounded-lg px-4 py-3">
                          <div>
                            <p className="text-gray-900 text-sm font-medium">{c.subject}</p>
                            <p className="text-gray-500 text-xs mt-0.5">Filter: {c.filter} | {new Date(c.sent_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-green-400 font-bold text-sm">{c.sent}</p>
                              <p className="text-gray-400 text-[10px]">Sent</p>
                            </div>
                            {c.failed > 0 && (
                              <div className="text-center">
                                <p className="text-red-400 font-bold text-sm">{c.failed}</p>
                                <p className="text-gray-400 text-[10px]">Failed</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contacts Table */}
              <Card className="bg-white border-gray-200" data-testid="contacts-table">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Imported Contacts ({contacts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {contacts.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No imported contacts yet. Users can import contacts from Google or Microsoft.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-500 text-xs border-b border-gray-200">
                            <th className="text-left py-2 px-3">Name</th>
                            <th className="text-left py-2 px-3">Email</th>
                            <th className="text-left py-2 px-3">Imported By</th>
                            <th className="text-left py-2 px-3">Status</th>
                            <th className="text-left py-2 px-3">Invited</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contacts.slice(0, 50).map((c, i) => (
                            <tr key={i} className="border-b border-gray-200/50 hover:bg-gray-50">
                              <td className="py-2 px-3 text-gray-900">{c.name || '—'}</td>
                              <td className="py-2 px-3 text-gray-600">{c.email || '—'}</td>
                              <td className="py-2 px-3 text-gray-500">{c.imported_by}</td>
                              <td className="py-2 px-3">
                                {c.is_registered ? (
                                  <span className="text-green-400 text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Registered</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">Not registered</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                {c.invite_sent ? (
                                  <span className="text-blue-400 text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Sent</span>
                                ) : (
                                  <span className="text-amber-400 text-xs">Pending</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {contacts.length > 50 && <p className="text-gray-400 text-xs mt-2 text-center">Showing 50 of {contacts.length} contacts</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CRM DATABASE TAB — Registered Users */}
          <TabsContent value="crm">
            <div className="space-y-6">
              <Card className="bg-white border-gray-200 border-2 border-blue-200 shadow-sm" data-testid="crm-database">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-green-400" />
                      CRM Database — All Registered Users ({crmUsers.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-500" />
                        <input
                          type="text"
                          placeholder="Search name, email, phone, city..."
                          value={crmSearch}
                          onChange={e => setCrmSearch(e.target.value)}
                          className="pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 text-xs w-64"
                          data-testid="crm-search"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Click column headers to sort. Full contact database for marketing campaigns.</p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="crm-table">
                      <thead>
                        <tr className="text-gray-500 text-xs border-b-2 border-gray-200">
                          {[
                            { key: 'full_name', label: 'Name' },
                            { key: 'email', label: 'Email' },
                            { key: 'phone', label: 'Phone' },
                            { key: 'company', label: 'Company' },
                            { key: 'profession', label: 'Profession' },
                            { key: 'city', label: 'City' },
                            { key: 'state', label: 'State' },
                            { key: 'subscription_status', label: 'Status' },
                            { key: 'created_at', label: 'Joined' },
                          ].map(col => (
                            <th key={col.key}
                              className="text-left py-2.5 px-3 cursor-pointer hover:text-gray-900 transition-colors select-none whitespace-nowrap"
                              onClick={() => setCrmSort(prev => ({ field: col.key, dir: prev.field === col.key && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                              data-testid={`crm-sort-${col.key}`}
                            >
                              <span className="flex items-center gap-1">
                                {col.label}
                                {crmSort.field === col.key ? (
                                  crmSort.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                                ) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const q = crmSearch.toLowerCase();
                          let filtered = crmUsers.filter(u =>
                            !q || (u.full_name || '').toLowerCase().includes(q) ||
                            (u.email || '').toLowerCase().includes(q) ||
                            (u.phone || '').toLowerCase().includes(q) ||
                            (u.company || '').toLowerCase().includes(q) ||
                            (u.city || '').toLowerCase().includes(q) ||
                            (u.state || '').toLowerCase().includes(q)
                          );
                          filtered.sort((a, b) => {
                            const av = (a[crmSort.field] || '').toString().toLowerCase();
                            const bv = (b[crmSort.field] || '').toString().toLowerCase();
                            return crmSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                          });
                          return filtered.length === 0 ? (
                            <tr><td colSpan={9} className="text-center py-8 text-gray-400">No users found</td></tr>
                          ) : filtered.map(u => (
                            <tr key={u.id} className="border-b border-gray-200/50 hover:bg-gray-50 transition-colors" data-testid={`crm-row-${u.id}`}>
                              <td className="py-2.5 px-3 text-gray-900 font-medium whitespace-nowrap">{u.full_name || '—'}</td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs">{u.email || '—'}</td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs">{u.phone || '—'}</td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs whitespace-nowrap">{u.company || '—'}</td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs whitespace-nowrap">
                                {u.profession || '—'}
                                {u.insurance_carrier && <span className="block text-gray-400 text-[10px]">{u.insurance_carrier} ({Array.isArray(u.insurance_specialty) ? u.insurance_specialty.join(', ').toUpperCase() : (u.insurance_specialty || '')})</span>}
                              </td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs">{u.city || '—'}</td>
                              <td className="py-2.5 px-3 text-gray-600 text-xs">{u.state || '—'}</td>
                              <td className="py-2.5 px-3">
                                {u.is_premium ? (
                                  <Badge className="bg-green-600 text-[10px]">Premium</Badge>
                                ) : u.subscription_status === 'trial' ? (
                                  <Badge className="bg-blue-600 text-[10px]">Trial</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-[10px]">Free</Badge>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-gray-400 text-xs whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* STRIPE SETUP TAB */}
          <TabsContent value="stripe">
            <Card className="bg-white border-gray-200">
              <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><CreditCard className="w-5 h-5" />Stripe Account Setup</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-green-400 mt-0.5" /><div><p className="text-green-400 font-medium">Stripe Live Mode Active</p><p className="text-gray-500 text-sm mt-1">Real payments are being processed. Stripe Connect enabled for automatic 50/50 revenue splits.</p></div></div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3"><Info className="w-5 h-5 text-blue-400 mt-0.5" /><div><p className="text-blue-400 font-medium">How Payments Work</p><p className="text-gray-500 text-sm mt-1">Subscriptions ($20/mo) go to ConnectMail. Ad slot purchases ($20) split 50/50 via Stripe Connect. Stripe fees (~2.9% + $0.30) apply.</p></div></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AMBASSADORS TAB */}
          <TabsContent value="ambassadors">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Ambassador Program
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Manage verified ambassadors who can feature events and represent the community
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const ambassadors = users.filter(u => u.is_ambassador);
                  const nonAmbassadors = users.filter(u => !u.is_ambassador);
                  return (
                    <div className="space-y-6">
                      {/* Current Ambassadors */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-500" />
                          Active Ambassadors ({ambassadors.length})
                        </h3>
                        {ambassadors.length > 0 ? (
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {ambassadors.map(u => (
                              <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50/50" data-testid={`ambassador-${u.id}`}>
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                                    <Star className="w-4 h-4 text-amber-700" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{u.full_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{u.company || u.email}</p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 text-xs flex-shrink-0"
                                  onClick={async () => {
                                    try {
                                      await api.adminToggleAmbassador(adminToken, u.id);
                                      setUsers(prev => prev.map(p => p.id === u.id ? {...p, is_ambassador: false} : p));
                                      toast.success(`Revoked ambassador status from ${u.full_name}`);
                                    } catch { toast.error('Failed to update'); }
                                  }}
                                  data-testid={`revoke-ambassador-${u.id}`}
                                >
                                  <X className="w-3.5 h-3.5 mr-1" />Revoke
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No ambassadors yet. Grant ambassador status to trusted members below.</p>
                        )}
                      </div>

                      {/* Grant Ambassador */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Grant Ambassador Status</h3>
                        <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left py-2 px-3 text-gray-600 font-medium">Member</th>
                                <th className="text-left py-2 px-3 text-gray-600 font-medium">Company</th>
                                <th className="text-right py-2 px-3 text-gray-600 font-medium">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {nonAmbassadors.map(u => (
                                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                                  <td className="py-2 px-3">
                                    <p className="font-medium text-gray-900">{u.full_name}</p>
                                    <p className="text-xs text-gray-400">{u.email}</p>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">{u.company || '-'}</td>
                                  <td className="py-2 px-3 text-right">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                      onClick={async () => {
                                        try {
                                          await api.adminToggleAmbassador(adminToken, u.id);
                                          setUsers(prev => prev.map(p => p.id === u.id ? {...p, is_ambassador: true} : p));
                                          toast.success(`${u.full_name} is now an ambassador!`);
                                        } catch { toast.error('Failed to update'); }
                                      }}
                                      data-testid={`grant-ambassador-${u.id}`}
                                    >
                                      <Star className="w-3 h-3 mr-1" />Grant
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VIDEO LIBRARY TAB */}
          <TabsContent value="videos">
            {/* AI Video Studio */}
            <Card className="bg-white border-gray-200 shadow-sm mb-6">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  AI Video Studio
                </CardTitle>
                <CardDescription>Create promotional, tutorial, demo, or testimonial videos for marketing.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Generation Method Toggle */}
                <div>
                  <label className="text-xs text-gray-600 mb-2 block font-medium">Generation Method</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setGenMethod('slideshow')}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        genMethod === 'slideshow'
                          ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-200'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      data-testid="method-slideshow"
                    >
                      <p className="text-xs font-semibold text-gray-900">AI Image Slideshow</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">GPT Image 1 scenes + smooth transitions. Consistent quality.</p>
                    </button>
                    <button
                      onClick={() => setGenMethod('sora2')}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        genMethod === 'sora2'
                          ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-200'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      data-testid="method-sora2"
                    >
                      <p className="text-xs font-semibold text-gray-900">Sora 2 AI Video</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">Full AI video generation. Longer wait, variable results.</p>
                    </button>
                  </div>
                </div>

                {/* Video Type Selector */}
                <div>
                  <label className="text-xs text-gray-600 mb-2 block font-medium">Video Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { id: 'promo', label: 'Promotional', icon: '🎬', desc: 'Landing page & ads' },
                      { id: 'tutorial', label: 'Tutorial', icon: '📖', desc: 'Step-by-step guide' },
                      { id: 'demo', label: 'Product Demo', icon: '🖥️', desc: 'Feature showcase' },
                      { id: 'testimonial', label: 'Testimonial', icon: '💬', desc: 'Social proof' },
                    ].map(vt => (
                      <button
                        key={vt.id}
                        onClick={() => { setVideoType(vt.id); setVideoPrompt(''); }}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          videoType === vt.id
                            ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'
                        }`}
                        data-testid={`video-type-${vt.id}`}
                      >
                        <div className="text-lg mb-1">{vt.icon}</div>
                        <p className="text-xs font-semibold text-gray-900">{vt.label}</p>
                        <p className="text-[10px] text-gray-400">{vt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration Selector - only for Sora 2 */}
                {genMethod === 'sora2' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block font-medium">Duration</label>
                    <select
                      value={videoDuration}
                      onChange={(e) => setVideoDuration(Number(e.target.value))}
                      className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm"
                      data-testid="video-duration-select"
                    >
                      <option value={4}>4 seconds</option>
                      <option value={8}>8 seconds</option>
                      <option value={12}>12 seconds</option>
                      <option value={16}>16 seconds (2 clips)</option>
                      <option value={20}>20 seconds (2 clips)</option>
                      <option value={24}>24 seconds (2 clips)</option>
                      <option value={28}>28 seconds (3 clips)</option>
                      <option value={32}>32 seconds (3 clips)</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <p className="text-xs text-gray-400"><Clock className="w-3 h-3 inline mr-1" />Longer videos take more time & credits</p>
                  </div>
                </div>
                )}

                {/* AI Prompt Creator - only for Sora 2 */}
                {genMethod === 'sora2' && (
                <>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block font-medium">Describe your video idea (AI will create the prompt)</label>
                  <div className="flex gap-2">
                    <Input
                      value={videoDescription}
                      onChange={(e) => setVideoDescription(e.target.value)}
                      placeholder="e.g. Show professionals exchanging business cards that transform into digital signatures..."
                      className="flex-1 text-sm"
                      data-testid="video-description-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={generatingPrompt}
                      className="shrink-0"
                      data-testid="ai-prompt-btn"
                      onClick={async () => {
                        setGeneratingPrompt(true);
                        try {
                          const data = await api.adminGenerateVideoPrompt(adminToken, { description: videoDescription, duration: videoDuration, video_type: videoType });
                          setVideoPrompt(data.prompt);
                          toast.success('AI prompt generated!');
                        } catch { toast.error('Failed to generate prompt'); }
                        finally { setGeneratingPrompt(false); }
                      }}
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                      {generatingPrompt ? 'Writing...' : 'AI Create'}
                    </Button>
                  </div>
                </div>

                {/* Editable Prompt */}
                <div>
                  <label className="text-xs text-gray-600 mb-1 block font-medium">Video Prompt (edit or let AI create it)</label>
                  <textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder={`Leave empty for default ${videoType} prompt, or type/paste your custom prompt...`}
                    rows={4}
                    className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm resize-none"
                    data-testid="video-prompt-textarea"
                  />
                </div>
                </>
                )}

                {/* Generate Button */}
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                  disabled={videoGenerating || !adminToken}
                  data-testid="generate-video-btn"
                  onClick={async () => {
                    setVideoGenerating(true);
                    setVideoStatus(null);
                    setElapsedTime(0);
                    if (timerRef.current) clearInterval(timerRef.current);
                    if (pollRef.current) clearInterval(pollRef.current);
                    const startTime = Date.now();
                    timerRef.current = setInterval(() => setElapsedTime(Math.floor((Date.now() - startTime) / 1000)), 1000);
                    try {
                      let data;
                      if (genMethod === 'slideshow') {
                        data = await api.adminGenerateSlideshowVideo(adminToken, { video_type: videoType });
                      } else {
                        data = await api.adminGenerateVideo(adminToken, { prompt: videoPrompt, duration: videoDuration, video_type: videoType });
                      }
                      setVideoJobId(data.job_id);
                      setVideoStatus({ status: 'generating', progress: data.message, percent: 5 });
                      localStorage.setItem('connectmail_video_job', JSON.stringify({ jobId: data.job_id, startedAt: startTime }));
                      pollRef.current = setInterval(async () => {
                        try {
                          const status = await api.adminGetVideoStatus(adminToken, data.job_id);
                          setVideoStatus(status);
                          if (status.status === 'completed' || status.status === 'failed') {
                            clearInterval(pollRef.current);
                            clearInterval(timerRef.current);
                            pollRef.current = null;
                            timerRef.current = null;
                            setVideoGenerating(false);
                            localStorage.removeItem('connectmail_video_job');
                            if (status.status === 'completed') {
                              toast.success(`${videoType.charAt(0).toUpperCase() + videoType.slice(1)} video generated!`);
                              setVideoExists(true);
                              const videosData = await api.adminListVideos(adminToken);
                              setGeneratedVideos(videosData.videos || []);
                            } else {
                              toast.error('Video generation failed: ' + status.progress);
                            }
                          }
                        } catch {
                          clearInterval(pollRef.current);
                          clearInterval(timerRef.current);
                          pollRef.current = null;
                          timerRef.current = null;
                          setVideoGenerating(false);
                          localStorage.removeItem('connectmail_video_job');
                        }
                      }, 5000);
                    } catch (err) {
                      const msg = err?.response?.data?.detail || err?.message || 'Failed to start video generation';
                      toast.error(msg);
                      setVideoGenerating(false);
                      localStorage.removeItem('connectmail_video_job');
                      clearInterval(timerRef.current);
                      timerRef.current = null;
                    }
                  }}
                >
                  {videoGenerating ? 'Generating...' : genMethod === 'slideshow'
                    ? `Generate ${videoType.charAt(0).toUpperCase() + videoType.slice(1)} Slideshow`
                    : `Generate ${videoDuration}s ${videoType.charAt(0).toUpperCase() + videoType.slice(1)} Video`
                  }
                </Button>

                {/* ===== PERSISTENT PROGRESS BAR ===== */}
                {videoGenerating && videoStatus && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-4 space-y-3" data-testid="video-progress-panel">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                        <span className="text-sm font-medium text-purple-800">Generating Video</span>
                      </div>
                      <span className="text-xs text-purple-600 font-mono" data-testid="elapsed-time">
                        {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2.5 overflow-hidden" data-testid="video-progress-bar">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-1000 ease-out"
                        style={{ width: `${videoStatus.percent || 5}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-purple-700" data-testid="video-progress-text">{videoStatus.progress}</p>
                      <span className="text-xs font-semibold text-purple-600">{videoStatus.percent || 5}%</span>
                    </div>
                    <p className="text-[10px] text-purple-400">
                      {genMethod === 'slideshow'
                        ? `AI is generating ${videoStatus.total_scenes || 6} scene images for your ${videoType} video. Do not close this page.`
                        : `Sora 2 AI is rendering your ${videoDuration}s ${videoType} video. Do not close this page.`
                      }
                    </p>
                  </div>
                )}

                {/* Completion/Failure Status */}
                {!videoGenerating && videoStatus && videoStatus.status !== 'generating' && (
                  <div className={`rounded-lg border p-3 flex items-start gap-2 ${
                    videoStatus.status === 'completed' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`} data-testid="video-result-status">
                    {videoStatus.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Info className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    )}
                    <p className={`text-xs ${videoStatus.status === 'completed' ? 'text-green-700' : 'text-red-700'}`}>
                      {videoStatus.progress}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Video Library */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Film className="w-5 h-5 text-purple-500" />
                  Video Library ({generatedVideos.length} videos)
                </CardTitle>
                <CardDescription>All generated marketing videos. Download, preview, or delete.</CardDescription>
              </CardHeader>
              <CardContent>
                {generatedVideos.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Film className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No videos generated yet. Use the AI Video Studio above to create your first video.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {generatedVideos.map(v => (
                      <div key={v.id || v.video_type} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50" data-testid={`library-video-${v.id || v.video_type}`}>
                        <VideoPlayer url={`${process.env.REACT_APP_BACKEND_URL}${v.video_url}`} testId={`library-video-preview-${v.id || v.video_type}`} />
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="text-sm font-semibold text-gray-900">{v.label} Video</h6>
                            <Badge variant="outline" className="text-xs">{v.method || 'unknown'}</Badge>
                          </div>
                          <p className="text-xs text-gray-400 mb-3">
                            {v.duration || '?'}s · {v.file_size_mb || '?'} MB
                            {v.created_at && ` · ${new Date(v.created_at).toLocaleDateString()}`}
                          </p>
                          {v.prompt && (
                            <p className="text-xs text-gray-500 mb-3 line-clamp-2 italic">{v.prompt}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${v.video_url}`} download={`connectmail_${v.video_type}_${v.id?.slice(0,6) || ''}.mp4`}>
                              <Button size="sm" variant="outline" data-testid={`library-download-${v.id || v.video_type}`}>
                                <Download className="w-3 h-3 mr-1" />Download
                              </Button>
                            </a>
                            {v.id && !v.id.startsWith('legacy_') && (
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`library-delete-${v.id}`}
                                onClick={async () => {
                                  if (!window.confirm('Delete this video permanently?')) return;
                                  try {
                                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/videos/${v.id}`, {
                                      method: 'DELETE',
                                      headers: { Authorization: `Bearer ${adminToken}` }
                                    });
                                    setGeneratedVideos(prev => prev.filter(gv => gv.id !== v.id));
                                    toast.success('Video deleted');
                                  } catch { toast.error('Failed to delete video'); }
                                }}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DEMO ANALYTICS TAB */}
          <TabsContent value="demos">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="demo-analytics-card">
              <CardHeader>
                <CardTitle className="text-gray-900">Landing-Page Demo Analytics</CardTitle>
                <CardDescription className="text-gray-500">
                  Anonymous tracking of demo modal interactions and signup attribution.
                  Use this to identify which value prop converts best.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!demoAnalytics && (
                  <div className="text-sm text-gray-500 py-6 text-center" data-testid="demo-analytics-loading">Loading…</div>
                )}
                {demoAnalytics && (
                  <>
                    {/* Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                      {[
                        { label: 'Total Views', value: demoAnalytics.totals.views, color: 'bg-blue-50 text-blue-700 border-blue-200', testid: 'demo-total-views' },
                        { label: 'Completed', value: demoAnalytics.totals.completed, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', testid: 'demo-total-completed' },
                        { label: 'Closed Early', value: demoAnalytics.totals.closed_early, color: 'bg-amber-50 text-amber-700 border-amber-200', testid: 'demo-total-early' },
                        { label: 'CTA Clicks', value: demoAnalytics.totals.cta_clicks, color: 'bg-purple-50 text-purple-700 border-purple-200', testid: 'demo-total-cta' },
                        { label: 'Signups', value: demoAnalytics.totals.signups, color: 'bg-rose-50 text-rose-700 border-rose-200', testid: 'demo-total-signups' },
                      ].map(stat => (
                        <div key={stat.label} className={`rounded-lg border p-3 ${stat.color}`} data-testid={stat.testid}>
                          <div className="text-2xl font-bold">{stat.value}</div>
                          <div className="text-xs font-medium uppercase tracking-wide opacity-80">{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Per-demo table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="demo-analytics-table">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr className="text-left text-gray-600">
                            <th className="px-3 py-2 font-semibold">Demo</th>
                            <th className="px-3 py-2 font-semibold text-right">Views</th>
                            <th className="px-3 py-2 font-semibold text-right">Completed</th>
                            <th className="px-3 py-2 font-semibold text-right">Completion</th>
                            <th className="px-3 py-2 font-semibold text-right">Early Close</th>
                            <th className="px-3 py-2 font-semibold text-right">CTA Clicks</th>
                            <th className="px-3 py-2 font-semibold text-right">CTA Rate</th>
                            <th className="px-3 py-2 font-semibold text-right">Signups</th>
                            <th className="px-3 py-2 font-semibold text-right">Signup Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {demoAnalytics.demos.map((d) => (
                            <tr key={d.demo_key} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`demo-row-${d.demo_key}`}>
                              <td className="px-3 py-2.5 font-semibold text-gray-900">{d.label}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{d.views}</td>
                              <td className="px-3 py-2.5 text-right font-mono">{d.completed}</td>
                              <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{d.completion_rate}%</td>
                              <td className="px-3 py-2.5 text-right font-mono text-amber-700">{d.early_close_rate}%</td>
                              <td className="px-3 py-2.5 text-right font-mono">{d.cta_clicks}</td>
                              <td className="px-3 py-2.5 text-right font-mono text-purple-700">{d.cta_rate}%</td>
                              <td className="px-3 py-2.5 text-right font-mono font-bold text-rose-700">{d.signups}</td>
                              <td className="px-3 py-2.5 text-right font-mono text-rose-700">{d.signup_rate}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 text-xs text-gray-500 leading-relaxed">
                      <strong>How to read this:</strong> "Signup Rate" = signups from <code>/register?ref=demo-{`<key>`}</code> ÷ total views.
                      The demo with the highest Signup Rate is your strongest landing-page value prop — double down on it in your ads.
                      Early Close rate tells you which demo loses attention fastest (consider tightening the script).
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FUNNEL TAB */}
          <TabsContent value="funnel">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="funnel-card">
              <CardHeader>
                <CardTitle className="text-gray-900">Registration Funnel</CardTitle>
                <CardDescription className="text-gray-500">
                  Every step from landing to first ad. Identify the biggest drop-off and optimise that step first.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {adminToken && <FunnelAnalytics adminToken={adminToken} />}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUCCESS STORIES TAB */}
          <TabsContent value="stories">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="stories-card">
              <CardHeader>
                <CardTitle className="text-gray-900">Landing-Page Success Stories</CardTitle>
                <CardDescription className="text-gray-500">
                  Upload commercial-quality MP4s or paste YouTube/Vimeo URLs. The landing page renders these as a
                  featured hero video + 4 supporting tiles. Edit copy here without touching code.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {adminToken && <SuccessStoriesAdmin adminToken={adminToken} />}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle>Edit User</DialogTitle><DialogDescription className="text-gray-500">Modify user details for {editUser?.full_name}</DialogDescription></DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1"><Label className="text-gray-600 text-sm">Full Name</Label><Input value={editForm.full_name || ''} onChange={(e) => setEditForm(p => ({ ...p, full_name: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" data-testid="edit-name-input" /></div>
            <div className="space-y-1"><Label className="text-gray-600 text-sm">Email</Label><Input value={editForm.email || ''} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" data-testid="edit-email-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-gray-600 text-sm">Company</Label><Input value={editForm.company || ''} onChange={(e) => setEditForm(p => ({ ...p, company: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" /></div>
              <div className="space-y-1"><Label className="text-gray-600 text-sm">Profession</Label><Input value={editForm.profession_id || ''} onChange={(e) => setEditForm(p => ({ ...p, profession_id: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-gray-600 text-sm">City</Label><Input value={editForm.city || ''} onChange={(e) => setEditForm(p => ({ ...p, city: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" /></div>
              <div className="space-y-1"><Label className="text-gray-600 text-sm">State</Label><Input value={editForm.state || ''} onChange={(e) => setEditForm(p => ({ ...p, state: e.target.value }))} className="bg-gray-50 border-gray-300 text-gray-900" /></div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Label className="text-gray-600 text-sm">Premium Status</Label>
              <button onClick={() => setEditForm(p => ({ ...p, is_premium: !p.is_premium }))} className={`relative w-11 h-6 rounded-full transition-colors ${editForm.is_premium ? 'bg-green-600' : 'bg-gray-200'}`} data-testid="edit-premium-toggle">
                <span className={`block w-4 h-4 rounded-full bg-white transition-transform absolute top-1 ${editForm.is_premium ? 'left-6' : 'left-1'}`} />
              </button>
              <span className="text-sm text-gray-500">{editForm.is_premium ? 'Premium' : 'Free'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleEditUser} disabled={actionLoading} data-testid="save-edit-btn">{actionLoading ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-red-400">Delete User Account</DialogTitle><DialogDescription className="text-gray-500">This action cannot be undone.</DialogDescription></DialogHeader>
          <div className="py-4"><p className="text-gray-600">Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteUser?.full_name}</span> ({deleteUser?.email})? This will remove their account, signatures, and all associated data.</p></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={actionLoading} data-testid="confirm-delete-btn">{actionLoading ? 'Deleting...' : 'Delete Account'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
